import { Component, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { IndicatorChartTypeEnums } from 'src/app/enum/indicator-chart-enums';
import { OrderStateEnums, OrderTypeEnums } from 'src/app/enum/order-enums';
import { RemissionOrderStateEnum } from 'src/app/enum/remission-order-state-enum';
import { WindowEnums } from 'src/app/enum/window-enums';
import { IndicatorChart } from 'src/app/interfaces/custom-indicators/indicator-chart.interface';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ItemChartModel } from 'src/app/models/charts/global/item-chart.model';
import { OrderChartModel } from 'src/app/models/charts/preliminaries/order-chart.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { FilterGlobalResponseModel } from 'src/app/models/responses/filter-global-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { OrderDetailTableModel } from 'src/app/models/tables/preliminaries/order-detail-table.model';
import { OrderTableModel } from 'src/app/models/tables/preliminaries/order-table.model';
import { RemissionFromOrderTableModel } from 'src/app/models/tables/rental/remission-from-order-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { OrderDetailService } from 'src/app/services/preliminaries/order-detail.service';
import { OrderService } from 'src/app/services/preliminaries/order.service';
import { RemissionFromOrderService } from 'src/app/services/rental/remission-from-order.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { inject } from '@angular/core';
import { OrderDataComponent } from '../../../rental/remission-detail/order-data/order-data.component';

const OrderIndicatorsConstants = {
  states: [
    { id: OrderStateEnums.open, label: 'Abiertos', color: '#EF4444' },
    { id: OrderStateEnums.initialClose, label: 'Cierre Inicial', color: '#14B8A6' },
    { id: OrderStateEnums.finalClose, label: 'Cierre Final', color: '#198754' },
    { id: OrderStateEnums.annulled, label: 'Anulados', color: '#3B82F6' }
  ],
  types: [
    { id: OrderTypeEnums.rental, label: 'Alquiler' },
    { id: OrderTypeEnums.newSale, label: 'Venta Nuevo' },
    { id: OrderTypeEnums.usedSale, label: 'Venta Usado' },
    { id: OrderTypeEnums.service, label: 'Servicios' }
  ]
};

@Component({
  selector: 'app-order',
  templateUrl: './order.component.html',
  styleUrls: ['./order.component.scss'],
  standalone: false
})
export class OrderComponent implements OnInit {

  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly headerService = inject(HeaderService);
  private readonly orderDetailService = inject(OrderDetailService);
  private readonly orderService = inject(OrderService);
  private readonly remissionFromOrderService = inject(RemissionFromOrderService);
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);

  @ViewChild('orderDataModal') orderDataModalComponent?: CustomModalComponent | null = null;

  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  componentOrderData = OrderDataComponent;
  detailData: PagedResponseModel<OrderDetailTableModel> =
    new PagedResponseModel<OrderDetailTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<OrderTableModel> =
    new PagedResponseModel<OrderTableModel>();
  generalHeader: ColumnResponseModel[] = [];
  indicatorCharts: IndicatorChart[] = this.buildIndicatorCharts(new OrderChartModel());
  indicatorsRequestId: number = 0;
  inputsForOrderDataModal$ = new BehaviorSubject<any>({
    orderId: 0
  });
  limit: number = 10;
  listRemissionOrderState: ColumnResponseModel =
    new ColumnResponseModel();
  loadingIndicators: boolean = false;
  nameWindow: string = 'Pedidos';
  origin: WindowEnums = WindowEnums.Order;
  page: number = 1;
  projectName = ProjectName.projectName;
  remissionFromOrderActions: TableAction[] = [
    {
      label: 'Datos del pedido',
      icon: 'pi pi-info-circle',
      action: 'order-data'
    }
  ];
  remissionFromOrderData: PagedResponseModel<RemissionFromOrderTableModel> =
    new PagedResponseModel<RemissionFromOrderTableModel>();
  remissionFromOrderHeader: FilterGlobalResponseModel =
    new FilterGlobalResponseModel();
  remissionOrderState: number = RemissionOrderStateEnum.pending;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    }
  ];
  url: string = 'order';

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem(this.url);
    if (filters) this.filters = JSON.parse(filters);
    this.getIndicatorsForGeneralConsultation();
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getAllOrdersWithPagination(),
    ]);
  }

  buildIndicatorCharts(indicators: OrderChartModel): IndicatorChart[] {
    const { states, types } = OrderIndicatorsConstants;
    return [
      {
        type: IndicatorChartTypeEnums.donut,
        title: 'Pedidos por estado',
        labels: states.map(state => state.label),
        values: states.map(state => this.getIndicatorQuantity(indicators.states, state.id)),
        colors: states.map(state => state.color),
        total: indicators.total
      },
      {
        type: IndicatorChartTypeEnums.bar,
        title: 'Pedidos por tipo',
        labels: types.map(type => type.label),
        values: types.map(type => this.getIndicatorQuantity(indicators.types, type.id))
      }
    ];
  }

  async bulkCloseOrders() {
    if (await this.confirmDialogService.confirm('¿Está seguro de que desea realizar el cierre masivo de pedidos?')) {
      try {
        await firstValueFrom(this.orderService.bulkCloseOrdersAsync());
        this.toastHandlerService.showToast(
          'success',
          'El cierre masivo de pedidos se realizó correctamente.'
        );
        await Promise.all([
          this.getAllOrdersWithPagination(),
          this.getIndicatorsForGeneralConsultation()
        ]);
      } catch (error) {
        console.error('Se produjo un error al realizar el cierre masivo de pedidos.', error);
      }
    }
  }

  async exportDetailConsultation() {
    try {
      const blob = await firstValueFrom(
        this.orderDetailService.exportDetailConsultation(
          this.filters, this.columnSort, this.ascendant
        )
      );
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Detalles Pedido.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar el detalle del pedido.', error);
    }
  }

  async exportGeneralConsultation() {
    try {
      const blob = await firstValueFrom(
        this.orderService.exportGeneralConsultation(
          this.filters, this.columnSort, this.ascendant
        )
      );
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Pedido.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar consulta general del pedido.', error);
    }
  }

  async exportRemissionFromOrderConsultation() {
    try {
      const blob = await firstValueFrom(
        this.remissionFromOrderService.exportRemissionFromOrderConsultation(
          this.filters, this.columnSort, this.ascendant, this.remissionOrderState, this.origin
        )
      );
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Remisión Pedidos.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar consulta pedido-remisión.', error);
    }
  }

  async getAllOrdersWithPagination() {
    try {
      this.generalData = await firstValueFrom(
        this.orderService.getAllOrdersWithPagination(
          this.page, this.limit, this.columnSort, this.ascendant, this.filters
        )
      );
    } catch (error) {
      console.error('Error al obtener la informacion del pedido.', error);
    }
  }

  async getAllOrderDetailsWithPagination() {
    try {
      this.detailData = await firstValueFrom(
        this.orderDetailService.getAllOrderDetailsWithPagination(
          this.page, this.limit, this.columnSort, this.ascendant, this.filters
        )
      );
    } catch (error) {
      console.error('Error al obtener la informacion de los detalles del pedido.', error);
    }
  }

  async getAllRemissionFromOrderWithPagination() {
    try {
      this.remissionFromOrderData = await firstValueFrom(
        this.remissionFromOrderService.getAllRemissionFromOrderWithPagination(
          this.page, this.limit, this.columnSort, this.ascendant, this.remissionOrderState, this.origin, this.filters
        )
      );
    } catch (error) {
      console.error('Error al buscar la información del pedido-remisión.', error);
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(
          this.orderDetailService.getColumnsForDetailConsultation()
        );
      }
    } catch (error) {
      console.error('Error al obtener las columnas para la consulta de detalle.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(
          this.orderService.getColumnsForGeneralConsultation()
        );
      }
      this.listRemissionOrderState = new ColumnResponseModel;
    } catch (error) {
      console.error('Error al obtener las columnas para la consulta general.', error);
    }
  }

  async getColumnsForRemissionFromOrderConsultation() {
    try {
      if (this.remissionFromOrderHeader.columns.length === 0) {
        this.remissionFromOrderHeader = await firstValueFrom(
          this.remissionFromOrderService.getColumnsForRemissionFromOrderConsultation()
        );
      }
      this.listRemissionOrderState = this.remissionFromOrderHeader.filtersGlobal[0];
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta pedido-remisión.', error);
    }
  }

  async getIndicatorsForGeneralConsultation() {
    // Descarta respuestas de solicitudes anteriores que lleguen después de la última
    const requestId = ++this.indicatorsRequestId;
    this.loadingIndicators = true;
    try {
      const indicators = await firstValueFrom(this.orderService.getIndicatorsForGeneralConsultation(this.filters));
      if (requestId === this.indicatorsRequestId) {
        this.indicatorCharts = this.buildIndicatorCharts(indicators);
      }
    } catch (error) {
      console.error('Error al buscar los indicadores de los pedidos.', error);
    } finally {
      if (requestId === this.indicatorsRequestId)
        this.loadingIndicators = false;
    }
  }

  getIndicatorQuantity(items: ItemChartModel[], id: number): number {
    return items?.find(item => item.id === id)?.quantity ?? 0;
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length = this.detailHeader.length === 1 ? 0 : this.detailHeader.length;
    this.remissionFromOrderHeader.columns.length = this.remissionFromOrderHeader.columns.length === 1 ? 0
      : this.remissionFromOrderHeader.columns.length;

    if (this.activeTabIndex === tabIndex) return;
    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;
    switch (this.activeTabIndex) {
      case 0:
        this.getIndicatorsForGeneralConsultation();
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getAllOrdersWithPagination(),
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getAllOrderDetailsWithPagination()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 2:
        await Promise.all([
          this.getColumnsForRemissionFromOrderConsultation(),
          this.getAllRemissionFromOrderWithPagination()
        ]);
        this.remissionFromOrderData = {
          ...this.remissionFromOrderData,
          currentPage: 1,
          pageSize: 10
        };
        break;
    }
  }

  newOrder() {
    this.router.navigate([`/${this.url}/register`]);
  }

  onAction(event: { action: string, item?: OrderTableModel | RemissionFromOrderTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/${this.url}/register/${event.item.orderId}`]);
    }
    if (event.action === 'order-data' && event.item) {
      this.openOrderDataModal(event.item.orderId);
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0:
        await this.exportGeneralConsultation();
        break;
      case 1:
        await this.exportDetailConsultation();
        break;
      case 2:
        await this.exportRemissionFromOrderConsultation();
        break;
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getAllOrdersWithPagination()
        break;
      case 1:
        await this.getAllOrderDetailsWithPagination()
        break;
      case 2:
        await this.getAllRemissionFromOrderWithPagination();
        break;
    }
  }

  onRemissionOrderStateChange(value: number) {
    this.remissionOrderState = value;
  }

  openOrderDataModal(orderId: number) {
    this.inputsForOrderDataModal$.next({
      orderId: orderId
    });
    this.orderDataModalComponent?.show();
  }

  processFilter(filter: any) {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }
}
